import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import CategoryCard from './CategoryCard';
import './SurveyAnalysis.css';

interface QuestionData {
  questionId: string;
  text: string;
  totalResponses: number;
  averageScore: number;
  percentPositive: number;
  distribution: Record<string, number>;
}

interface CategoryData {
  categoryId: string;
  categoryName: string;
  order: number;
  questionCount: number;
  totalResponses: number;
  averageScore: number;
  percentPositive: number;
  healthIndicator: 'green' | 'yellow' | 'red';
  questions: QuestionData[];
}

interface SurveyAnalysisProps {
  surveyType: string;
  school?: string;
  respondentType?: string;
}

const surveyTypeLabels: Record<string, string> = {
  'k-2': 'K-2 Student Survey',
  '3-5': 'Grades 3-5 Student Survey',
  '6-8': 'Grades 6-8 Student Survey',
  'high-school': 'High School Student Survey',
  'virtual-k8': 'Virtual K-8 Student Survey',
  'virtual-hs': 'Virtual High School Student Survey',
  'staff-climate': 'Staff Climate Survey',
};

const SurveyAnalysis: React.FC<SurveyAnalysisProps> = ({ surveyType, school, respondentType = 'student' }) => {
  const { token } = useAuth();
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !surveyType) return;

      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({ surveyType, role: respondentType });
        if (school) {
          params.append('school', school);
        }

        const response = await fetch(`/api/admin/stats/categories?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch category data');
        }

        const data = await response.json();
        setCategories(data.categories || []);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token, surveyType, school, respondentType]);

  if (isLoading) {
    return (
      <div className="survey-analysis__loading">
        <div className="survey-analysis__spinner" />
        <p>Loading survey analysis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="survey-analysis__error">
        <p>Error: {error}</p>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="survey-analysis__empty">
        <p>No category data available for this survey type.</p>
      </div>
    );
  }

  // Calculate overall metrics
  const overallResponses = categories.reduce((sum, c) => sum + c.totalResponses, 0) / categories.length;
  const overallPercent = categories.reduce((sum, c) => sum + c.percentPositive, 0) / categories.length;
  const overallAvg = categories.reduce((sum, c) => sum + c.averageScore, 0) / categories.length;

  return (
    <div className="survey-analysis">
      <div className="survey-analysis__header">
        <div className="survey-analysis__title-row">
          <h2>{surveyTypeLabels[surveyType] || surveyType}</h2>
          {school && <span className="survey-analysis__school-filter">Filtered: {school}</span>}
        </div>
        <div className="survey-analysis__overview">
          <div className="survey-analysis__stat">
            <span className="survey-analysis__stat-value">{Math.round(overallPercent)}%</span>
            <span className="survey-analysis__stat-label">Overall Positive</span>
          </div>
          <div className="survey-analysis__stat">
            <span className="survey-analysis__stat-value">{overallAvg.toFixed(1)}</span>
            <span className="survey-analysis__stat-label">Average Score</span>
          </div>
          <div className="survey-analysis__stat">
            <span className="survey-analysis__stat-value">~{Math.round(overallResponses)}</span>
            <span className="survey-analysis__stat-label">Avg Responses</span>
          </div>
          <div className="survey-analysis__stat">
            <span className="survey-analysis__stat-value">{categories.length}</span>
            <span className="survey-analysis__stat-label">Categories</span>
          </div>
        </div>
      </div>

      <div className="survey-analysis__categories">
        {categories.map((category) => (
          <CategoryCard
            key={category.categoryId}
            categoryId={category.categoryId}
            categoryName={category.categoryName}
            questionCount={category.questionCount}
            totalResponses={category.totalResponses}
            averageScore={category.averageScore}
            percentPositive={category.percentPositive}
            healthIndicator={category.healthIndicator}
            questions={category.questions}
          />
        ))}
      </div>
    </div>
  );
};

export default SurveyAnalysis;
